import { AdminLayout } from "@/components/layout/AdminLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Search, Loader2 } from "lucide-react";
import { useState, useEffect, useCallback, useRef } from "react";
import { adminService, UserSearchQueryRow } from "../../services/adminService";
import { showErrorToast } from "@/utils/errorHandler";

const SEARCH_TYPE_FILTER_OPTIONS: { value: string; label: string }[] = [
  { value: "", label: "All scopes" },
  { value: "all", label: "all" },
  { value: "title", label: "title" },
  { value: "author", label: "author" },
  { value: "publisher", label: "publisher" },
  { value: "file_name", label: "file_name" },
  { value: "keyword", label: "keyword" },
  { value: "classification", label: "classification" },
  { value: "category", label: "category" },
  { value: "sr_no", label: "sr_no" },
  { value: "ocr", label: "ocr" },
  { value: "quantum", label: "quantum" },
  { value: "agam", label: "agam" },
];

const formatBrowser = (name?: string | null, ver?: string | null) =>
  [name, ver].filter(Boolean).join(" ").trim() || "—";
const formatOs = (name?: string | null, ver?: string | null) =>
  [name, ver].filter(Boolean).join(" ").trim() || "—";

const UserSearchHistory = () => {
  const [rows, setRows] = useState<UserSearchQueryRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [scopeFilter, setScopeFilter] = useState("");
  const [pagination, setPagination] = useState({
    count: 0,
    page: 1,
    pageSize: 10,
  });

  const fetchRows = useCallback(
    async (page = 1, search = "", explicitScope?: string) => {
      const scope = explicitScope ?? scopeFilter;
      try {
        setLoading(true);
        const params: Parameters<typeof adminService.getUserSearchQueries>[0] =
          {
            page,
            page_size: pagination.pageSize,
            search: search || undefined,
            ordering: "-created_at",
          };
        if (scope) {
          params.search_type = scope;
        }
        const data = await adminService.getUserSearchQueries(params);
        setRows(data.results);
        setPagination(prev => ({
          ...prev,
          count: data.count,
          page,
        }));
      } catch (error: unknown) {
        console.error("Error fetching user search history:", error);
        showErrorToast(
          error,
          "Failed to load search history. Please try again."
        );
      } finally {
        setLoading(false);
      }
    },
    [pagination.pageSize, scopeFilter]
  );

  const fetchedRef = useRef(false);
  useEffect(() => {
    if (fetchedRef.current) {
      return;
    }
    fetchedRef.current = true;
    fetchRows(1, "");
    return () => {
      fetchedRef.current = false;
    };
  }, [fetchRows]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    fetchRows(1, searchTerm);
  };

  const handleScopeChange = (value: string) => {
    setScopeFilter(value);
    fetchRows(1, searchTerm, value);
  };

  const totalPages = Math.ceil(pagination.count / pagination.pageSize);

  return (
    <AdminLayout>
      <div className="space-y-4 md:space-y-6">
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <h2 className="text-xl font-bold text-foreground md:text-3xl">
            User Search History
          </h2>
          <form
            onSubmit={handleSearch}
            className="flex w-full flex-col gap-2 sm:max-w-xl sm:flex-row sm:items-center"
          >
            <div className="relative min-w-0 flex-1">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                type="search"
                placeholder="Search query, email, browser, UA…"
                className="w-full pl-9"
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
              />
            </div>
            <select
              className="h-10 rounded-md border border-input bg-background px-3 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              value={scopeFilter}
              onChange={e => handleScopeChange(e.target.value)}
              aria-label="Filter by search scope"
            >
              {SEARCH_TYPE_FILTER_OPTIONS.map(opt => (
                <option key={opt.value || "all-scopes"} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
            <Button
              type="submit"
              variant="secondary"
              className="shrink-0 bg-[#533a1d] text-white hover:bg-[#533a1d]/90"
            >
              Search
            </Button>
          </form>
        </div>

        <div className="overflow-hidden rounded-lg border border-border bg-card shadow-sm">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="border-b border-border bg-muted/50">
                <tr>
                  <th className="w-16 px-4 py-3 text-left font-semibold uppercase tracking-wider text-muted-foreground">
                    ID
                  </th>
                  <th className="whitespace-nowrap px-4 py-3 text-left font-semibold uppercase tracking-wider text-muted-foreground">
                    Date
                  </th>
                  <th className="w-20 px-4 py-3 text-left font-semibold uppercase tracking-wider text-muted-foreground">
                    User ID
                  </th>
                  <th className="min-w-[140px] px-4 py-3 text-left font-semibold uppercase tracking-wider text-muted-foreground">
                    Email
                  </th>
                  <th className="min-w-[100px] px-4 py-3 text-left font-semibold uppercase tracking-wider text-muted-foreground">
                    Username
                  </th>
                  <th className="px-4 py-3 text-left font-semibold uppercase tracking-wider text-muted-foreground">
                    Search Type
                  </th>
                  <th className="min-w-[200px] px-4 py-3 text-left font-semibold uppercase tracking-wider text-muted-foreground">
                    Query
                  </th>
                  <th className="min-w-[120px] px-4 py-3 text-left font-semibold uppercase tracking-wider text-muted-foreground">
                    IP
                  </th>
                  <th className="min-w-[100px] px-4 py-3 text-left font-semibold uppercase tracking-wider text-muted-foreground">
                    City
                  </th>
                  <th className="min-w-[80px] px-4 py-3 text-left font-semibold uppercase tracking-wider text-muted-foreground">
                    State
                  </th>
                  <th className="min-w-[80px] px-4 py-3 text-left font-semibold uppercase tracking-wider text-muted-foreground">
                    Country
                  </th>
                  <th className="min-w-[96px] px-4 py-3 text-left font-semibold uppercase tracking-wider text-muted-foreground">
                    Browser
                  </th>
                  <th className="min-w-[96px] px-4 py-3 text-left font-semibold uppercase tracking-wider text-muted-foreground">
                    OS
                  </th>
                  <th className="min-w-[72px] px-4 py-3 text-left font-semibold uppercase tracking-wider text-muted-foreground">
                    Device
                  </th>
                  <th className="min-w-[120px] px-4 py-3 text-left font-semibold uppercase tracking-wider text-muted-foreground">
                    UA
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {loading && rows.length === 0 ? (
                  <tr>
                    <td colSpan={15} className="px-4 py-12 text-center">
                      <div className="flex flex-col items-center gap-2 text-muted-foreground">
                        <Loader2 className="h-8 w-8 animate-spin" />
                        <p>Loading search history...</p>
                      </div>
                    </td>
                  </tr>
                ) : rows.length === 0 ? (
                  <tr>
                    <td
                      colSpan={15}
                      className="px-4 py-12 text-center text-muted-foreground"
                    >
                      No search records found.
                    </td>
                  </tr>
                ) : (
                  rows.map(item => (
                    <tr
                      key={item.id}
                      className="transition-colors hover:bg-muted/30"
                    >
                      <td className="px-4 py-3 font-medium">{item.id}</td>
                      <td className="whitespace-nowrap px-4 py-3">
                        {new Date(item.created_at).toLocaleString()}
                      </td>
                      <td className="px-4 py-3">{item.user_id}</td>
                      <td className="max-w-[220px] truncate px-4 py-3">
                        {item.user_email}
                      </td>
                      <td className="max-w-[140px] truncate px-4 py-3">
                        {item.user_username}
                      </td>
                      <td className="whitespace-nowrap px-4 py-3">
                        {item.search_type || "all"}
                      </td>
                      <td
                        className="max-w-md truncate px-4 py-3"
                        title={item.query}
                      >
                        {item.query}
                      </td>
                      <td className="max-w-[140px] truncate px-4 py-3 font-mono text-xs">
                        {item.ip_address ?? "—"}
                      </td>
                      <td className="max-w-[100px] truncate px-4 py-3">
                        {item.city ?? "—"}
                      </td>
                      <td className="max-w-[80px] truncate px-4 py-3">
                        {item.state ?? "—"}
                      </td>
                      <td className="max-w-[80px] truncate px-4 py-3">
                        {item.country ?? "—"}
                      </td>
                      <td
                        className="max-w-[120px] truncate px-4 py-3 text-xs"
                        title={formatBrowser(
                          item.browser_name,
                          item.browser_version
                        )}
                      >
                        {formatBrowser(item.browser_name, item.browser_version)}
                      </td>
                      <td
                        className="max-w-[120px] truncate px-4 py-3 text-xs"
                        title={formatOs(item.os_name, item.os_version)}
                      >
                        {formatOs(item.os_name, item.os_version)}
                      </td>
                      <td className="whitespace-nowrap px-4 py-3 text-xs capitalize">
                        {item.device_type ?? "—"}
                      </td>
                      <td
                        className="max-w-[160px] truncate px-4 py-3 font-mono text-[10px]"
                        title={item.user_agent ?? undefined}
                      >
                        {item.user_agent
                          ? `${item.user_agent.slice(0, 48)}${
                              item.user_agent.length > 48 ? "…" : ""
                            }`
                          : "—"}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="flex flex-col items-center justify-between gap-4 md:flex-row">
          <div className="text-sm text-muted-foreground">
            {pagination.count > 0 ? (
              <>
                Showing{" "}
                <span className="font-medium text-foreground">
                  {(pagination.page - 1) * pagination.pageSize + 1}
                </span>{" "}
                to{" "}
                <span className="font-medium text-foreground">
                  {Math.min(
                    pagination.page * pagination.pageSize,
                    pagination.count
                  )}
                </span>{" "}
                of{" "}
                <span className="font-medium text-foreground">
                  {pagination.count}
                </span>{" "}
                results
              </>
            ) : (
              "No results to show"
            )}
          </div>
          <div className="flex flex-wrap justify-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => fetchRows(1, searchTerm)}
              disabled={pagination.page === 1 || loading}
            >
              « First
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => fetchRows(pagination.page - 1, searchTerm)}
              disabled={pagination.page === 1 || loading}
            >
              ‹ Back
            </Button>
            <div className="flex h-9 items-center rounded-md border bg-muted/50 px-4 text-sm font-medium">
              Page {pagination.page} of {totalPages || 1}
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => fetchRows(pagination.page + 1, searchTerm)}
              disabled={pagination.page >= totalPages || loading}
            >
              Next ›
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => fetchRows(totalPages, searchTerm)}
              disabled={pagination.page >= totalPages || loading}
            >
              Last »
            </Button>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default UserSearchHistory;
